import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { referralAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Loader2, Copy, Check, Gift, Users, Trophy, Share2, 
  ChevronRight, Clock, CheckCircle, Star
} from 'lucide-react';

const Referrals = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referralInfo, setReferralInfo] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [copied, setCopied] = useState(false);
  const [applyCode, setApplyCode] = useState('');
  const [applying, setApplying] = useState(false);

  const isEmployer = user?.account_type === 'employer';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [infoRes, leaderboardRes] = await Promise.all([
        referralAPI.getInfo(),
        referralAPI.getLeaderboard()
      ]);
      setReferralInfo(infoRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
    } catch (error) {
      console.error('Error fetching referral info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralInfo?.referral_code || '');
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralInfo?.referral_link || '');
      toast.success('Referral link copied!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join VETT',
          text: `Use my referral code ${referralInfo?.referral_code} to sign up for VETT - the verified work history platform.`,
          url: referralInfo?.referral_link
        });
      } catch (error) {
        // User cancelled sharing
      }
    } else {
      handleCopyLink();
    }
  };

  const handleApplyCode = async () => {
    if (!applyCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }

    setApplying(true);
    try {
      const res = await referralAPI.applyCode(applyCode.trim());
      toast.success(res.data.message);
      setApplyCode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply code');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  const stats = referralInfo?.stats || {};
  const rewards = referralInfo?.rewards || [];
  const recentReferrals = referralInfo?.referrals || [];

  return (
    <DashboardLayout>
      <div className="max-w-4xl space-y-6" data-testid="referrals-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">Referral Program</h1>
          <p className="mt-1 text-[#5B6472] text-sm">
            {isEmployer 
              ? 'Invite others and earn extra trial days for each successful referral.'
              : 'Invite others and earn free Pro access for each successful referral.'
            }
          </p>
        </div>

        {/* Reward Banner */}
        <Card className="bg-gradient-to-r from-[#1B5AFF]/20 to-[#FFFFFF]/30 border border-[#1B5AFF]/30 rounded-xl p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-[#1B5AFF]/20 flex items-center justify-center flex-shrink-0">
              <Gift className="w-7 h-7 text-[#1B5AFF]" />
            </div>
            <div className="flex-1">
              <h2 className="text-white font-semibold text-lg mb-1">Your Reward</h2>
              <p className="text-[#0A0F1E]">
                {isEmployer 
                  ? <><span className="text-[#1B5AFF] font-bold">+14 days</span> added to your trial for each referral</>
                  : <><span className="text-[#1B5AFF] font-bold">+30 days</span> of free Pro for each referral</>
                }
              </p>
            </div>
          </div>
        </Card>

        {/* Your Referral Code */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4 flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#1B5AFF]" />
            Your Referral Code
          </h2>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 bg-[#FFFFFF]/50 border border-[#E8EDF5] rounded-xl px-4 py-3 flex items-center justify-between">
              <span className="text-white font-mono text-lg tracking-wider">
                {referralInfo?.referral_code || 'Loading...'}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyCode}
                className="text-[#1B5AFF] hover:bg-[#1B5AFF]/10"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <Button
              onClick={handleShare}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF]/90 text-white rounded-xl px-6"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>

          <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-2 text-sm">
            <span className="text-[#5B6472]">Or share your link:</span>
            <button 
              onClick={handleCopyLink}
              className="text-[#1B5AFF] hover:underline truncate max-w-full"
            >
              {referralInfo?.referral_link}
            </button>
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-[#0A0F1E]">{stats.total_referrals || 0}</p>
            <p className="text-[#5B6472] text-sm">Total Referrals</p>
          </Card>
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-green-400">{stats.successful_referrals || 0}</p>
            <p className="text-[#5B6472] text-sm">Successful</p>
          </Card>
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-amber-400">{stats.pending_referrals || 0}</p>
            <p className="text-[#5B6472] text-sm">Pending</p>
          </Card>
        </div>

        {/* Apply Referral Code */}
        {!referralInfo?.referral_code_used && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Have a Referral Code?</h2>
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                value={applyCode}
                onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
                placeholder="Enter code (e.g., VR-ABC123)"
                className="bg-[#FFFFFF]/50 border-[#E8EDF5] text-white rounded-xl h-11 flex-1 font-mono"
              />
              <Button
                onClick={handleApplyCode}
                disabled={applying}
                className="bg-[#1B5AFF] hover:bg-[#1B5AFF]/90 text-white rounded-xl px-6"
              >
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply Code'}
              </Button>
            </div>
          </Card>
        )}

        {/* Recent Referrals */}
        {recentReferrals.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#1B5AFF]" />
              Recent Referrals
            </h2>
            <div className="space-y-3">
              {recentReferrals.map((ref) => (
                <div 
                  key={ref.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-[#FFFFFF]/30"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      ref.status === 'completed' ? 'bg-green-500/20' : 'bg-amber-500/20'
                    }`}>
                      {ref.status === 'completed' 
                        ? <CheckCircle className="w-4 h-4 text-green-400" />
                        : <Clock className="w-4 h-4 text-amber-400" />
                      }
                    </div>
                    <div>
                      <p className="text-[#0A0F1E] text-sm">{ref.referred_email?.replace(/(.{2}).*@/, '$1***@')}</p>
                      <p className="text-[#5B6472] text-xs capitalize">{ref.referred_account_type}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    ref.status === 'completed' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {ref.status}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Rewards Earned */}
        {rewards.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-400" />
              Rewards Earned
            </h2>
            <div className="space-y-2">
              {rewards.map((reward) => (
                <div 
                  key={reward.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-[#FFFFFF]/30"
                >
                  <div className="flex items-center gap-3">
                    <Gift className="w-5 h-5 text-[#1B5AFF]" />
                    <span className="text-[#0A0F1E]">{reward.description}</span>
                  </div>
                  <span className="text-[#5B6472] text-xs">
                    {new Date(reward.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              Top Referrers
            </h2>
            <div className="space-y-2">
              {leaderboard.map((entry, i) => (
                <div 
                  key={i}
                  className="flex items-center justify-between p-3 rounded-lg bg-[#FFFFFF]/30"
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      i === 0 ? 'bg-amber-500 text-black' :
                      i === 1 ? 'bg-gray-400 text-black' :
                      i === 2 ? 'bg-amber-700 text-white' :
                      'bg-[#E8EDF5] text-[#5B6472]'
                    }`}>
                      {entry.rank}
                    </span>
                    <div>
                      <p className="text-[#0A0F1E] text-sm">{entry.email_masked}</p>
                      <p className="text-[#5B6472] text-xs capitalize">{entry.account_type}</p>
                    </div>
                  </div>
                  <span className="text-[#1B5AFF] font-bold">{entry.referral_count}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Referrals;
